import {
  useGlobalNotifier,
  useMessage,
  useSpreadsheetState,
  useUserDetails,
} from "@vrf/fr-vrf-core-react";
import { FrVrfSpreadsheet } from "@vrf/fr-vrf-spreadsheet-react";
import {
  FrVrfButton,
  FrVrfButtonPanel,
  FrVrfCheckboxField,
  FrVrfCheckboxesField,
  FrVrfCodeInputSearchField,
  FrVrfFooter,
  FrVrfFormPanel,
  FrVrfIcon,
  FrVrfInputDaterangeField,
  FrVrfMultipleSelectorField,
  FrVrfPanel,
  FrVrfSingleLineInputField,
} from "@vrf/fr-vrf-web-components-react";
import React, { useEffect, useMemo } from "react";
import { useState } from "@vrf/fr-vrf-core-react";
import { createPath, useNavigate } from "react-router-dom";
import { usePageState } from ".";
import { useAppState } from "../../app";
import { ApplicationMessageScreen } from "../../messages";
import { useGetDips, useGetUsers } from "../../services/data-list";
import { Pjt, useGetProjects } from "../../services/projects";
import { isProperUser } from "../../util/auth-util";
import { ENV } from "@vrf/fr-vrf-core";
import {
  InDefCommonFlagType,
  PjtLnchType,
} from "../../util/general-code/enums/general-codes";
import {
  ComponentSearchDialogContent,
  useComponentSearchDialogState,
} from "../dialog/component-search-dialog";
import {
  UserSearchDialogContent,
  useUserSearchDialogState,
} from "../dialog/user-search-dialog";
import { usePageNavigation } from "../use-page-navigation";

const PROJECT_SEARCH_LIST_TEMPLATE_FILE_URL =
  "spreadsheet-template/project-search-list.xlsx";
const SHOW_NUM = 5;
const PROJECT_IN_PROGRESS = "Project In Progress";
const CHANGING_IF_REQUIREMENTS = "Changing IF Requirements";
const PROJECT_COMPLETE = "Project Complete";

export const ProjectSearchPageContent = () => {
  const { isModalPage } = usePageNavigation();
  const [appState, setAppState] = useAppState();
  const [pageState, setPageState] = usePageState();
  const { userDetails } = useUserDetails();
  const navigate = useNavigate();

  const { messageForScreen } = useMessage();

  const [userSearchDialogState, setUserSearchDialogState] =
    useUserSearchDialogState();
  const [componentSearchDialogState, setComponentSearchDialogState] =
    useComponentSearchDialogState();

  const [dipItems, setDipItems] = useState<
    { label: string; value: any; selected: boolean }[]
  >([]);
  const [dipDisplayItems, setDipDisplayItems] = useState<string[]>([""]);
  const [prdDplymntInputDateRange, setPrdDplymntInputDateRange] = useState<{
    startDate: string;
    endDate: string;
  }>({ startDate: "", endDate: "" });
  const [stgDplymntInputDateRange, setStgDplymntInputDateRange] = useState<{
    startDate: string;
    endDate: string;
  }>({ startDate: "", endDate: "" });
  const [pjtRgstrInputDateRange, setPjtRgstrInputDateRange] = useState<{
    startDate: string;
    endDate: string;
  }>({ startDate: "", endDate: "" });
  const { errorMessages, showToast, resetUserError } = useGlobalNotifier();

  const [getDipListResponse, sendGetDipListRequest] = useGetDips();
  const [getUsersByUserIdResponse, sendGetUsersByUserIdRequest] = useGetUsers();
  const [getProjectsResponse, sendGetProjectsRequest] = useGetProjects();

  const [spreadsheetState, setSpreadsheetState, initSpreadsheetState] =
    useSpreadsheetState<{ projectList: Pjt[] }>();

  const spreadsheetInitialData = useMemo(() => {
    const projectList = pageState.searchResult.projectList;
    return {
      projectList: projectList,
    };
  }, [
    pageState.searchResult,
    // Control spreadsheet size by showing or not displaying search form
    pageState.displayedSearchForm,
  ]);

  const onInitialDisplayed = () => {
    resetUserError();
    setAppState({ ...appState, title: "Project Search" });
    sendGetDipListRequest({});
  };

  const onGetDipListResponded = () => {
    if (!getDipListResponse) return;

    const dipListItems =
      getDipListResponse?.dipList.map((e) => {
        return { value: e.dip, label: e.dip, selected: false };
      }) || [];
    setDipItems(dipListItems);
  };

  const onGetUsersByUserIdResponded = () => {
    if (!getUsersByUserIdResponse) return;

    const userList = getUsersByUserIdResponse?.userList || [];
    if (userList.length > 0) {
      setPageState((prevState) => ({
        ...prevState,
        userName: userList[0].userName,
      }));
    }
  };

  const onGetProjectsResponded = () => {
    if (!getProjectsResponse) return;
    const projectList = getProjectsResponse?.pjtList ?? [];
    setPageState((prevState) => ({
      ...prevState,
      searchResult: {
        ...prevState,
        projectList: projectList,
      },
    }));
  };

  const onUserIdChanged = () => {
    resetUserError();
    const userId = pageState.userId;
    if (userId === "") return;
    sendGetUsersByUserIdRequest({ userId: userId });
  };

  const isChecked = (value: string) => value === InDefCommonFlagType.YES;

  const onListenCloseUserSearchDialogState = (): void => {
    if (!userSearchDialogState) return;
    if (
      userSearchDialogState.isShow === false &&
      userSearchDialogState.isApply === true
    ) {
      setPageState((prevState) => ({
        ...prevState,
        userId: userSearchDialogState.selectedUserId ?? "",
        userName: userSearchDialogState.selectedUserName ?? "",
      }));
    }
  };

  const onListenCloseComponentSearchDialogState = (): void => {
    if (!componentSearchDialogState) return;
    if (componentSearchDialogState.isShow === false) {
      setPageState((prevState) => ({
        ...prevState,
        cpntIdList: componentSearchDialogState.selectedCpntId ?? [],
      }));
    }
  };

  useEffect(onInitialDisplayed, []);
  useEffect(onGetDipListResponded, [getDipListResponse]);
  useEffect(onUserIdChanged, [pageState.userId]);
  useEffect(onGetUsersByUserIdResponded, [getUsersByUserIdResponse]);
  useEffect(onGetProjectsResponded, [getProjectsResponse]);
  useEffect(onListenCloseUserSearchDialogState, [userSearchDialogState.isShow]);
  useEffect(onListenCloseComponentSearchDialogState, [
    componentSearchDialogState.isShow,
  ]);

  const onValueChangedHandle = (name: string) => (event: CustomEvent) => {
    if (
      [
        "pjtNameOrDesc",
        "pjtStsList",
        "pjtLnchTypeList",
        "userId",
        "isPmyOnly",
        "dipList",
      ].includes(name)
    ) {
      setPageState((prevState) => ({
        ...prevState,
        [name]: event.detail.value,
      }));
      if (name === "userId" && event.detail.value === "") {
        setPageState((prevState) => ({
          ...prevState,
          userId: "",
          userName: "",
        }));
      }
      if (name === "dipList") {
        setDipDisplayItems(event.detail.value);
      }
    } else if ("cpntIdList" == name) {
      // in case of crearButton clicked.
      const cpntIdList: string[] = [event.detail.value];
      setPageState((prevState) => ({
        ...prevState,
        [name]: cpntIdList,
      }));
      setComponentSearchDialogState((prevState) => ({
        ...prevState,
        selectedCpntId: cpntIdList,
      }));
    } else if (
      [
        "prdDplymntDateRange",
        "stgDplymntDateRange",
        "pjtRgstrDateRange",
      ].includes(name)
    ) {
      setPageState((prevState) => ({
        ...prevState,
        [name]: event.detail,
      }));
      if (name === "prdDplymntDateRange") {
        setPrdDplymntInputDateRange({
          ...prdDplymntInputDateRange,
          startDate: event.detail.startDate,
          endDate: event.detail.endDate,
        });
      }
      if (name === "stgDplymntDateRange") {
        setStgDplymntInputDateRange({
          ...prdDplymntInputDateRange,
          startDate: event.detail.startDate,
          endDate: event.detail.endDate,
        });
      }
      if (name === "pjtRgstrDateRange") {
        setPjtRgstrInputDateRange({
          ...prdDplymntInputDateRange,
          startDate: event.detail.startDate,
          endDate: event.detail.endDate,
        });
      }
    }
  };

  const onCellClicked = (event: CustomEvent) => {
    const eventName = event.detail.name;
    if (eventName === "pjtName") {
      const projectId = event.detail.value.pjtId;
      if (!projectId) return;
      const href = createPath({
        pathname: `/project-plan?pjtId=${projectId}`,
      });
      navigate(href);
      return;
    }
    if (eventName === "pjtName_newTabIcon") {
      const projectId = event.detail.value.pjtId;
      if (!projectId) return;
      const href = createPath({
        pathname: `/project-plan?pjtId=${projectId}`,
      });
      window.open(window.location.pathname + "#" + href);
      return;
    }
  };

  const onSearchIconClicked = (name: string) => () => {
    if (name === "user") {
      setUserSearchDialogState((prevState) => ({
        ...prevState,
        isShow: true,
        defaultUserId: pageState.userId,
      }));
    } else if (name === "component") {
      setComponentSearchDialogState((prevState) => ({
        ...prevState,
        isShow: true,
      }));
    }
  };

  const createButtonClickedHandle = (event: CustomEvent) => {
    const href = createPath({
      pathname: "/project-plan",
    });
    navigate(href);
  };

  const searchButtonClickedHandle = (event: CustomEvent) => {
    resetUserError();
    if (pageState.pjtStsList.length === 0) {
      showToast({
        design: "alert",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00117, [
          "Project Status",
        ]),
      });
      return;
    }
    if (pageState.pjtLnchTypeList.length === 0) {
      showToast({
        design: "alert",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00117, [
          "Project Launch Type",
        ]),
      });
      return;
    }
    const pjtLnchTypeList = [] as string[];
    if (pageState.pjtLnchTypeList.includes("Regular")) {
      pjtLnchTypeList.push(PjtLnchType.REGULAR);
    }
    if (pageState.pjtLnchTypeList.includes("Irregular")) {
      pjtLnchTypeList.push(PjtLnchType.GOD);
    }
    sendGetProjectsRequest({
      pjtNameOrDesc: pageState.pjtNameOrDesc,
      prdDplymntDateRangFrom: pageState.prdDplymntDateRange.startDate,
      prdDplymntDateRangTo: pageState.prdDplymntDateRange.endDate,
      stgDplymntDateRangFrom: pageState.stgDplymntDateRange.startDate,
      stgDplymntDateRangTo: pageState.stgDplymntDateRange.endDate,
      pjtRgstrDateRangFrom: pageState.pjtRgstrDateRange.startDate,
      pjtRgstrDateRangTo: pageState.pjtRgstrDateRange.endDate,
      pjtStsList: pageState.pjtStsList,
      pjtLnchTypeList: pjtLnchTypeList,
      dipList: pageState.dipList,
      userId: pageState.userId,
      isPmyOnly: pageState.isPmyOnly ? InDefCommonFlagType.YES : "",
      cpntIdList: pageState.cpntIdList,
      dfnSetVerNum: ENV("REACT_APP_DFN_SET_VER_NUM"),
    });
  };

  const multiItemError = (item: string[]) => {
    let errorMessage = "";
    item.forEach((e) => {
      if (errorMessages(e, isModalPage())) {
        errorMessage =
          errorMessage + e + ":" + errorMessages(e, isModalPage()) + ",";
      }
    });
    return errorMessage.substring(0, errorMessage.length - 1);
  };

  return (
    <>
      {userSearchDialogState.isShow && (
        <>
          <UserSearchDialogContent />
        </>
      )}
      {componentSearchDialogState.isShow && (
        <>
          <ComponentSearchDialogContent />
        </>
      )}
      <FrVrfIcon
        onIconClicked={() => {
          setPageState((prevState) => ({
            ...prevState,
            displayedSearchForm: !prevState.displayedSearchForm,
          }));
        }}
        icon={
          pageState.displayedSearchForm
            ? "frIcon:chevron-up"
            : "frIcon:chevron-down"
        }
        authActId="warehouse_list.calendar"
        style={{
          "--vrf-icon-size": "25px",
          padding: "8px",
        }}
      />
      {pageState.displayedSearchForm && (
        <FrVrfPanel style={{ marginBottom: "10px" }}>
          <FrVrfFormPanel columns={4}>
            <FrVrfSingleLineInputField
              labelContent="Project Name / Description"
              content={pageState.pjtNameOrDesc}
              style={{
                "--vrf-single-line-input-field-label-width": "150px",
                "--vrf-single-line-input-field-input-width": "200px",
              }}
              onInputValueChanged={onValueChangedHandle("pjtNameOrDesc")}
              errorMessage={errorMessages(
                pageState.fieldPath.pjtNameOrDesc,
                isModalPage(),
              )}
            />
            <FrVrfInputDaterangeField
              labelContent="PRD Deployment Date"
              inputDateRange={prdDplymntInputDateRange}
              dateFormat="ymd"
              clearButtonVisible={false}
              style={{
                "--vrf-input-daterange-field-label-width": "150px",
                "--vrf-input-daterange-field-input-width": "200px",
              }}
              onInputValueChanged={onValueChangedHandle("prdDplymntDateRange")}
              errorMessage={multiItemError([
                pageState.fieldPath.prdDplymntDateRangFrom,
                pageState.fieldPath.prdDplymntDateRangTo,
              ])}
            />
            <FrVrfInputDaterangeField
              labelContent="STG Deployment Date"
              inputDateRange={stgDplymntInputDateRange}
              dateFormat="ymd"
              clearButtonVisible={false}
              style={{
                "--vrf-input-daterange-field-label-width": "150px",
                "--vrf-input-daterange-field-input-width": "200px",
              }}
              onInputValueChanged={onValueChangedHandle("stgDplymntDateRange")}
              errorMessage={multiItemError([
                pageState.fieldPath.stgDplymntDateRangFrom,
                pageState.fieldPath.stgDplymntDateRangTo,
              ])}
            />
            <FrVrfInputDaterangeField
              labelContent="Project Registered Date"
              inputDateRange={pjtRgstrInputDateRange}
              dateFormat="ymd"
              clearButtonVisible={false}
              style={{
                "--vrf-input-daterange-field-label-width": "150px",
                "--vrf-input-daterange-field-input-width": "200px",
              }}
              onInputValueChanged={onValueChangedHandle("pjtRgstrDateRange")}
              errorMessage={multiItemError([
                pageState.fieldPath.pjtRgstrDateRangFrom,
                pageState.fieldPath.pjtRgstrDateRangTo,
              ])}
            />
          </FrVrfFormPanel>
          <FrVrfFormPanel columns={4}>
            <FrVrfCheckboxesField
              labelContent="Project Status"
              required
              errorMessage={errorMessages(
                pageState.fieldPath.pjtStsList,
                isModalPage(),
              )}
              options={[
                { label: "Open", value: "1" },
                { label: "Closed", value: "2" },
              ]}
              checkList={pageState.pjtStsList}
              onCheckboxesChanged={onValueChangedHandle("pjtStsList")}
            />
            <FrVrfCheckboxesField
              labelContent="Project Launch Type"
              required
              errorMessage={errorMessages(
                pageState.fieldPath.pjtLnchTypeList,
                isModalPage(),
              )}
              options={[
                { label: "Regular", value: "Regular" },
                { label: "Irregular", value: "Irregular" },
              ]}
              checkList={pageState.pjtLnchTypeList}
              onCheckboxesChanged={onValueChangedHandle("pjtLnchTypeList")}
            />
            <FrVrfMultipleSelectorField
              label="DIP"
              selectItems={dipItems}
              displayedItems={dipDisplayItems}
              showNum={SHOW_NUM}
              getsEmptyWhenAllSelected={false}
              required={false}
              onItemsChanged={onValueChangedHandle("dipList")}
              errorMessage={errorMessages(
                pageState.fieldPath.dipList,
                isModalPage(),
              )}
            />
          </FrVrfFormPanel>
          <FrVrfFormPanel columns={2}>
            <div>
              <FrVrfCodeInputSearchField
                labelContent="Stakeholder"
                shouldShowClearButton={false}
                inputContent={pageState.userId}
                searchContent={pageState.userName}
                style={{
                  "--vrf-code-input-search-field-label-width": "150px",
                  "--vrf-code-input-search-field-input-width": "150px",
                  "--vrf-code-input-search-field-search-width": "200px",
                }}
                onInputValueChanged={onValueChangedHandle("userId")}
                onButtonClicked={onSearchIconClicked("user")}
                errorMessage={errorMessages(
                  pageState.fieldPath.userId,
                  isModalPage(),
                )}
              />
              <FrVrfCheckboxField
                content="Primary Only"
                checked={isChecked(pageState.isPmyOnly)}
                onCheckedChanged={onValueChangedHandle("isPmyOnly")}
                errorMessage={errorMessages(
                  pageState.fieldPath.isPmyOnly,
                  isModalPage(),
                )}
              />
            </div>
            <div>
              <FrVrfFormPanel columns={2}>
                <FrVrfSingleLineInputField
                  labelContent="Component"
                  clearButtonVisible={true}
                  type="search"
                  content={pageState.cpntIdList.map((c) => c).join(", ")}
                  onIconClicked={onSearchIconClicked("component")}
                  onInputValueChanged={onValueChangedHandle("cpntIdList")}
                  errorMessage={errorMessages(
                    pageState.fieldPath.cpntIdList,
                    isModalPage(),
                  )}
                />
                <FrVrfButtonPanel>
                  <FrVrfButton
                    content="Search"
                    theme="primary"
                    size="medium"
                    style={{ display: "flex", alignItems: "flex-end" }}
                    onClicked={searchButtonClickedHandle}
                  />
                </FrVrfButtonPanel>
              </FrVrfFormPanel>
            </div>
          </FrVrfFormPanel>
        </FrVrfPanel>
      )}
      <FrVrfSpreadsheet
        data={spreadsheetInitialData}
        onInitialized={initSpreadsheetState}
        templateUrl={PROJECT_SEARCH_LIST_TEMPLATE_FILE_URL}
        onCellClicked={onCellClicked}
        maxHeight={pageState.displayedSearchForm ? 480 : 800}
      />
      <FrVrfFooter>
        <FrVrfButton
          content="Create New Project"
          theme="secondary"
          size="large"
          visible={isProperUser(userDetails)}
          onClicked={createButtonClickedHandle}
        />
      </FrVrfFooter>
    </>
  );
};
